import React, { useEffect, useMemo, ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { getCandidatePreviewThunk } from '../../../redux/Candidate/Candidate.thunk';
import {
  getCandidateSelectedForPreview,
  resetCandidateSelectedForPreviewAction,
} from '../../../redux/Candidate/Candidate.slice';

import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../../../utils/getUserRolesByToken';
import { getUrlBasedOnCurrentPath } from './utils';

import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import useAuthHook from '../../../hooks/useAuth.hook';

import CandidateInfoBlockComponent from '../../Blocks/CandidateInfoBlock.component';
import CandidateRecordInfoBlockComponent from '../../Blocks/CandidateRecordInfoBlock.component';
import ButtonComponent from '../../Button.component';

import { UserPermissions } from '../../../types/userPermissions.types';
import { DATE_TIME_FORMAT } from '../../../constants/date.contant';
import { RoutesConstant } from '../../../constants/routes.constant';
import dayjs from 'dayjs';
import BaseModalComponent, { BaseModalProps } from '../BaseModal.component';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import AttachmentsBlockComponent from '../../Blocks/AttachmentsBlockComponent.component';
import { FavouriteButton } from '../../Buttons/FavouriteButton/FavouriteButton.component';
import { UserRole } from '../../../types/userRole.types';
import { LocationState } from '../../../types/locationState.interface';

interface Props extends BaseModalProps {
  personId: string | number;
  additionalAction?: ReactElement;
  animationStyle?: any;
  candidateGroup?: string;
  updateInfo?: any;
  closeUpperModal?: any;
}

const StyledCandidateRecordStatusStatus = styled.div`
  color: ${({ theme }) => theme.colors.LABEL};
`;

const StyledCandidateRecordStatusSeparator = styled.div`
  background: ${({ theme }) => theme.colors.BORDER};
  border-radius: 3px;
  width: 6px;
  height: 6px;
`;

const StyledCandidateRecordStatusEvent = styled.div``;

const StyledCandidateRecordStatus = styled.div`
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-top: -10px;
  gap: 10px;
`;

const StyledCandidateRecordDate = styled.div`
  font-weight: 600;
`;

const StyledCandidateRecord = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  font-size: 14px;
`;

const StyledCandidateLastRecordTitle = styled.div`
  display: flex;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
`;

const StyledCandidateLastRecord = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledCandidatesWrapperActions = styled.div`
  display: flex;
  width: 100%;
  gap: 10px;
  margin-top: 10px;
`;

const StyledCandidateModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
  width: 400px;
`;

export const StyledCandidateInfoTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
`;
export const StyledCandidateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledCandidateHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const CandidatePreviewModalComponent = ({
  personId,
  candidateGroup,
  additionalAction,
  updateInfo,
  closeUpperModal,
  ...restProps
}: Props) => {
  const { closeModals, closeAllModals } = useModalManager();
  const dispatch = useAppDispatch();
  const info = useAppSelector(getCandidateSelectedForPreview);
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuthHook();
  const navigate = useNavigate();

  useEffect(() => {
    if (!personId) return;
    const request = dispatch(getCandidatePreviewThunk(personId));

    return () => {
      request.abort();
      dispatch(resetCandidateSelectedForPreviewAction(undefined));
    };
  }, [personId]);

  useEffect(() => {
    const handlePopState = () => {
      closeModals();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const renderHeader = useMemo(() => {
    return (
      <StyledCandidateHeader>
        <>{info.item?.fullName}</>
        {checkUserRequiredRole(user.roles, [
          UserRole.HEAD,
          UserRole.DEV_HEAD,
        ]) &&
          info.item?.isFavourite !== undefined && (
            <FavouriteButton
              id={info.item?.id}
              isFavourite={info.item?.isFavourite}
              updateInfo={updateInfo}
            />
          )}
      </StyledCandidateHeader>
    );
  }, [info.item]);

  const renderContent = useMemo(() => {
    return (
      <StyledCandidateModal>
        {info.item && (
          <CandidateInfoBlockComponent
            links={info.item && info.item.links ? info.item.links : []}
            experience={info.item && info.item.experience}
            email={info.item && info.item.email}
            phone={info.item && info.item.phone}
            tags={info.item && info.item.tags ? info.item.tags : []}
            metaRegions={
              info.item && info.item.metaRegions ? info.item.metaRegions : []
            }
            english={info.item && info.item.english ? info.item.english : null}
            hasUnreachableRegions={info.item.hasUnreachableRegions}
            hasUnreachableRegionsVarning={false}
          />
        )}
        {checkUserRequiredPermissions(user.permissions, [
          UserPermissions.CAN_VIEW_CANDIDATE_RECORD,
        ]) &&
          info.item &&
          info.item.lastRecord && (
            <>
              <StyledCandidateLastRecord>
                <StyledCandidateLastRecordTitle>
                  {t('Last record')}
                </StyledCandidateLastRecordTitle>
                <StyledCandidateRecord>
                  <StyledCandidateRecordDate>
                    {dayjs
                      .unix(info.item.lastRecord.date)
                      .format(DATE_TIME_FORMAT)}
                  </StyledCandidateRecordDate>
                  {info.item.lastRecord.status && (
                    <StyledCandidateRecordStatus>
                      <StyledCandidateRecordStatusEvent
                        style={{
                          color: info.item.lastRecord.status.event.color,
                        }}
                      >
                        {info.item.lastRecord.status.event.name}
                      </StyledCandidateRecordStatusEvent>
                      <StyledCandidateRecordStatusSeparator></StyledCandidateRecordStatusSeparator>
                      <StyledCandidateRecordStatusStatus
                        style={{ color: info.item.lastRecord.status.color }}
                      >
                        {info.item.lastRecord.status.name}
                      </StyledCandidateRecordStatusStatus>
                    </StyledCandidateRecordStatus>
                  )}
                  <CandidateRecordInfoBlockComponent
                    withContentButton={false}
                    info={info.item.lastRecord}
                  />
                </StyledCandidateRecord>
              </StyledCandidateLastRecord>
              {info?.item?.attachments?.length > 0 && (
                <StyledCandidateInfo>
                  <StyledCandidateInfoTitle>
                    {t('Attachments')}:
                  </StyledCandidateInfoTitle>
                  <AttachmentsBlockComponent
                    attachments={info.item.attachments}
                  />
                </StyledCandidateInfo>
              )}
            </>
          )}
      </StyledCandidateModal>
    );
  }, [info.item]);

  const renderFooter = useMemo(() => {
    if (info.item) {
      const personUrl = getUrlBasedOnCurrentPath(
        location,
        personId,
        RoutesConstant.CANDIDATES_GROUP,
        'all',
      );

      const handleNavigateToCandidateProfile = () => {
        !!personUrl &&
          navigate(personUrl, {
            state: {
              backUrl: location.pathname,
              backUrlQuery: location.search,
            } as LocationState,
          });
        closeAllModals();
        closeUpperModal && closeUpperModal();
      };

      return (
        <StyledCandidatesWrapperActions>
          <ButtonComponent
            handleClick={handleNavigateToCandidateProfile}
            appearance="primary"
          >
            {t('Open candidate')}
          </ButtonComponent>
          {additionalAction}
        </StyledCandidatesWrapperActions>
      );
    } else {
      return undefined;
    }
  }, [info.item, location]);

  return (
    <BaseModalComponent
      header={info.item ? renderHeader : undefined}
      footer={renderFooter}
      style={restProps.animationStyle}
    >
      {renderContent}
    </BaseModalComponent>
  );
};

export default CandidatePreviewModalComponent;
